import { useEffect, useState } from 'react';
import { CloseCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Access, BundleAccess, SubscriptionAccess } from '@prisma/client';
import { Alert, App, Button, Descriptions, Modal, Space, Spin, Tabs, TabsProps, Typography } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';
import { delay } from 'lodash';

import { isAgsEntitlementAccess, isAgsRoleAccess, isIemAccess } from '@app/domain-access-interfaces';
import { SubscriptionWithChildren } from '@app/domain-subscription-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Tables } from '@app/web-components';

import { SubscriptionAccessStatusRenderer } from '../../admin/reports/access-status';
import { EligibilityRenderer, useBundleViewPanel } from '../../bundles';
import { AgsAccessNameRenderer, EmployeeTypeRenderer, IemAccessNameRenderer, UserPopover } from '../../common';
import { useFindSubscriptionById, useSubscriptionAccessTriggerRepush } from '../data';
import { useSubscriptionPermissions } from '../forms/rules/use-subscription-permissions';

import { ApproverTable } from './approver-table';

const DETAIL_TAB = 'DETAIL_TAB';
const BUNDLE_DETAIL_TAB = 'BUNDLE_DETAIL_TAB';

export type DetailModalProps = {
  subscriptionId: number | undefined; // parent should toggle to show/hide the dialog
  onClose: () => void;
};

export const DetailModal = ({ subscriptionId = 0, onClose }: DetailModalProps) => {
  const subscriptionQuery = useFindSubscriptionById({ subscriptionId, enabled: !!subscriptionId });

  const { getAccessTableProps, getPanelSpaceProps } = useBundleViewPanel(subscriptionQuery.data?.bundleId);

  const reload = () => {
    subscriptionQuery.refetch();
  };

  const tabs: TabsProps['items'] = [
    {
      key: DETAIL_TAB,
      label: `Details`,
      children: (
        <div style={{ height: '100%', overflow: 'auto' }}>
          {subscriptionQuery.data ? <DetailPanel subscription={subscriptionQuery.data} onReload={reload} /> : undefined}
        </div>
      ),
    },
    {
      key: BUNDLE_DETAIL_TAB,
      label: `Bundle Detail`,
      children: (
        <div style={{ height: '100%', overflow: 'auto' }}>
          <Space
            {...getPanelSpaceProps({
              children: <Table {...getAccessTableProps()} />,
            })}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Bundle '${subscriptionQuery.data?.bundle.name}' For '${subscriptionQuery.data?.user.name}'`}
      centered
      width={1100}
      styles={{
        body: {
          height: 600,
          overflow: 'auto',
        },
      }}
      open={!!subscriptionId}
      onCancel={onClose}
      footer={[
        <Button key="refresh" onClick={reload} icon={<ReloadOutlined />}>
          Refresh
        </Button>,
        <Button key="close" onClick={onClose} icon={<CloseCircleOutlined />} type="primary">
          Close
        </Button>,
      ]}
    >
      {subscriptionQuery.isPending ? <Spin /> : undefined}
      {subscriptionQuery.data ? <Tabs defaultActiveKey={DETAIL_TAB} items={tabs} /> : undefined}
      {subscriptionQuery.error ? (
        <Alert
          message="Error"
          description={`Something went wrong. Error: ${subscriptionQuery.error}`}
          type="error"
          showIcon
        />
      ) : undefined}
    </Modal>
  );
};

export type DetailPanelProps = {
  subscription: SubscriptionWithChildren;
  onReload: () => void;
};

export const DetailPanel = ({ subscription, onReload }: DetailPanelProps) => {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Descriptions bordered size="small" layout="horizontal" column={2}>
        <Descriptions.Item label="Bundle">{subscription.bundle.name}</Descriptions.Item>
        <Descriptions.Item label="Subscribed For">
          <UserPopover user={subscription.user}>
            <Button type="link" size="small" style={{ padding: 0 }}>
              {subscription.user.name}
            </Button>
          </UserPopover>
        </Descriptions.Item>
        <Descriptions.Item label="Subscribed By">{subscription.createdByUser.name}</Descriptions.Item>
        <Descriptions.Item label="Subscribed On">{formatIsoDate(subscription.createdOn.toString())}</Descriptions.Item>
        <Descriptions.Item span={2} label="Justification">
          {subscription.justification}
        </Descriptions.Item>
      </Descriptions>
      <AccessStatusTablePanel subscription={subscription} onReload={onReload} />
    </Space>
  );
};

export type AccessStatusTablePanelProps = {
  subscription: SubscriptionWithChildren;
  onReload: () => void;
};

export const AccessStatusTablePanel = ({ subscription, onReload }: AccessStatusTablePanelProps) => {
  const { canRetryAccess } = useSubscriptionPermissions();
  const enableRetryAccess = canRetryAccess(subscription);

  const columns: ColumnsType<
    SubscriptionAccess & {
      bundleAccess: BundleAccess & { access: Access };
    }
  > = [
    {
      title: 'Subscribed',
      dataIndex: 'subscribed',
      key: 'subscribed',
      render: (subscribed) => <Tables.BooleanRenderer value={subscribed} />,
    },
    {
      title: 'Eligible',
      key: 'eligible',
      width: 60,
      render: (_, record) => <EligibilityRenderer isEligible={record.isEligible} eligibility={record.eligibility} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => {
        if (isAgsRoleAccess(record.bundleAccess.access) || isAgsEntitlementAccess(record.bundleAccess.access)) {
          return <AgsAccessNameRenderer agsAccess={record.bundleAccess.access} />;
        } else if (isIemAccess(record.bundleAccess.access)) {
          return <IemAccessNameRenderer iemAccess={record.bundleAccess.access} />;
        } else {
          return name;
        }
      },
    },
    {
      title: 'Source',
      dataIndex: 'source',
      render: (_, record) => <>{record.bundleAccess.access.accessSource} </>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => (
        <SubscriptionAccessStatusRenderer
          access={record.bundleAccess.access}
          status={record.status}
          user={subscription.user}
        />
      ),
    },
    {
      title: 'Status Description',
      dataIndex: 'statusDescription',
    },
  ];

  if (enableRetryAccess) {
    columns.unshift({
      title: 'Action',
      width: 100,
      render: (_, record) => <RetryButton subscriptionAccessId={record.id} onReload={onReload} />,
    });
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Access Statuses
      </Typography.Title>
      <Table
        expandable={{
          expandedRowRender: (record) => (
            <Descriptions
              bordered
              size="small"
              layout="horizontal"
              labelStyle={{ backgroundColor: 'inherit' }}
              contentStyle={{ backgroundColor: 'white' }}
              column={2}
            >
              <Descriptions.Item label="Employee Type" span={2}>
                <EmployeeTypeRenderer employeeType={record.bundleAccess.access.employeeType} />
              </Descriptions.Item>
              <Descriptions.Item label="Approvers" span={2}>
                <ApproverTable
                  accessSource={record.bundleAccess.access.accessSource}
                  externalId={record.bundleAccess.access.externalId}
                  name={record.bundleAccess.access.name}
                  style={{ marginLeft: -40 }}
                />
              </Descriptions.Item>
            </Descriptions>
          ),
        }}
        rowKey={(e) => e.id}
        columns={columns}
        dataSource={subscription.accesses.sort((a, b) => a.id - b.id)}
        size="small"
        pagination={false}
      ></Table>
    </Space>
  );
};

type RetryButtonProps = {
  subscriptionAccessId: number;
  onReload: () => void;
};

export const RetryButton = ({ subscriptionAccessId, onReload }: RetryButtonProps) => {
  const [retryDelay, setRetryDelay] = useState(false);
  const { notification } = App.useApp();
  const subscriptionAccessRepush = useSubscriptionAccessTriggerRepush();

  useEffect(() => {
    if (subscriptionAccessRepush.isSuccess) {
      notification.success({
        message: 'Retry Success',
        description:
          'Retry was successful.  Click the refresh button to watch progress. Note that progress normally depends on approvals and could take some time.',
      });
    }

    if (subscriptionAccessRepush.isError) {
      const error = subscriptionAccessRepush.error as AxiosError;

      notification.error({
        message: 'Retry Error, please try again.',
        description: error.message,
        duration: 0, // force user to close
      });
    }
  }, [
    notification,
    subscriptionAccessRepush.error,
    subscriptionAccessRepush.isError,
    subscriptionAccessRepush.isSuccess,
  ]);

  return (
    <Button
      size="small"
      icon={<ReloadOutlined />}
      title="Click this to retry a request.  Normally this is used if the first request was rejected and the subscriber wants to try again or if something went wrong with the request."
      onClick={() => {
        setRetryDelay(true);
        delay(() => {
          subscriptionAccessRepush.mutate(subscriptionAccessId, { onSettled: () => onReload() });
          setRetryDelay(false);
        }, 800);
      }}
      loading={retryDelay || subscriptionAccessRepush.isPending}
    >
      Retry
    </Button>
  );
};
